import { useState, useEffect, useRef } from "react";
import { TailSpin } from "react-loader-spinner";
import "../styles/shareToQuickBooks.css";
import { createPortal } from "react-dom";
import { useQuickBooks } from "./QuickbooksContext";
import { toast } from "react-toastify";
import VendorModal from "./VendorModal";

const ShareToQuickBooks = ({
  reportId,
  spreadsheetId,
  quickbooks,
  isConnected: initialConnectionStatus,
  onStatusUpdate,
  onConnectionStatusChange,
  isClientOfAccountant,
  clientEmail,
  reportMetadata,
}) => {
  const { isConnected, verifyConnection, setIsConnected } = useQuickBooks();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const hasInitialVerified = useRef(false);

  const [isSharing, setIsSharing] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false); // Add this new state
  const [vendorDisplayName, setVendorDisplayName] = useState(null);
  const [validationErrors, setValidationErrors] = useState(null);
  const [vendorSelections, setVendorSelections] = useState([]);

  const [error, setError] = useState(null);
  const [mappingResults, setMappingResults] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [validationIssues, setValidationIssues] = useState(null);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);

  // Vendor Modal
  const [vendorModalOpen, setVendorModalOpen] = useState(false);
  const [vendorToCheck, setVendorToCheck] = useState(null);
  const [vendorSuggestions, setVendorSuggestions] = useState([]);
  const [pendingVendors, setPendingVendors] = useState([]);
  const [currentVendorIndex, setCurrentVendorIndex] = useState(0);

  // And add an effect to fetch it if needed
  useEffect(() => {
    // Fetch the vendor display name if needed
    const fetchVendorName = async () => {
      try {
        const response = await fetch("/api/user/vendor-name", {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setVendorDisplayName(data.vendorName);
        }
      } catch (error) {
        console.error("Error fetching vendor name:", error);
      }
    };

    if (isConnected) {
      fetchVendorName();
    }
  }, [isConnected]);

  useEffect(() => {
    setIsSharing(false);
  }, []); // Reset isSharing on mount

  // Only verify once on mount, not on every render
  useEffect(() => {
    if (!hasInitialVerified.current) {
      hasInitialVerified.current = true;
      verifyConnection();
    }
  }, [verifyConnection]);

  // In ShareToQuickBooks.jsx - Replace the existing handleVendorCreation function
  const handleVendorCreation = async (vendors) => {
    if (!vendors || vendors.length === 0) {
      return [];
    }

    console.log("Starting vendor creation workflow with merchants:", vendors);

    // Reset vendor state variables before starting
    setVendorSelections([]);

    // Create a local array to track selections that we'll pass around
    // instead of depending on the React state
    const localSelections = new Array(vendors.length).fill(null);

    // Set state for UI purposes, but don't depend on it for logic
    setVendorSelections(localSelections);

    // Set state
    setPendingVendors(vendors);
    setCurrentVendorIndex(0);

    // Use the local selections array instead of relying on state
    await checkVendor(vendors[0], vendors, 0, localSelections);
  };

  const fetchReportMetadata = async (reportId) => {
    try {
      // Add clientEmail parameter when in accountant view
      const url =
        isClientOfAccountant && clientEmail
          ? `/api/reports/${reportId}/metadata?clientEmail=${encodeURIComponent(
              clientEmail
            )}`
          : `/api/reports/${reportId}/metadata`;

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!response.ok) {
        console.warn("Could not fetch report metadata");
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error("Error fetching report metadata:", error);
      return null;
    }
  };

  const checkVendor = async (
    vendorName,
    vendorsArray = null,
    vendorIndex = null,
    localSelections = null
  ) => {
    try {
      // Use passed values or state if not provided
      const currentVendorIdx =
        vendorIndex !== null ? vendorIndex : currentVendorIndex;
      const currentPendingVendors = vendorsArray || [...pendingVendors];
      const currentSelections = localSelections || [...vendorSelections];

      console.log(`Starting vendor check for: ${vendorName}`, {
        currentIndex: currentVendorIdx,
        totalVendors: currentPendingVendors.length,
        pendingVendors: currentPendingVendors,
      });

      const response = await fetch("/api/quickbooks/check-vendor", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ vendorName }),
      });

      const data = await response.json();
      console.log("Vendor check response:", data);

      if (response.status === 401 && data.reauthorizationUrl) {
        window.location.href = data.reauthorizationUrl;
        return;
      }

      // Check if the response indicates the vendor was found
      const vendorFound = data.found || !!data.QueryResponse?.Vendor?.[0];

      if (vendorFound) {
        console.log(`Vendor ${vendorName} found, storing and moving to next`);

        // Extract vendor data from the correct location in the response
        let vendorId = null;
        let vendorDisplayName = null;

        if (data.vendor) {
          vendorId = data.vendor.id || data.vendor.Id;
          vendorDisplayName = data.vendor.name || data.vendor.DisplayName;
        } else if (data.QueryResponse?.Vendor?.[0]) {
          vendorId = data.QueryResponse.Vendor[0].Id;
          vendorDisplayName = data.QueryResponse.Vendor[0].DisplayName;
        }

        console.log(
          `Extracted vendor data: ID=${vendorId}, Name=${vendorDisplayName}`
        );

        // Store this found vendor in our local array
        currentSelections[currentVendorIdx] = {
          id: vendorId,
          name: vendorDisplayName,
        };

        // Update state with our modified copy - for UI display only
        setVendorSelections([...currentSelections]);

        // Log the updated selections array to verify
        console.log(
          "Updated selection array:",
          JSON.stringify(currentSelections)
        );

        // Move to next vendor or process
        const nextIndex = currentVendorIdx + 1;

        if (nextIndex < currentPendingVendors.length) {
          // We have more vendors to process, update index and check next
          setCurrentVendorIndex(nextIndex);
          // Pass the current localSelections array forward
          await checkVendor(
            currentPendingVendors[nextIndex],
            currentPendingVendors,
            nextIndex,
            currentSelections // Pass the local copy
          );
        } else {
          // All vendors processed, continue sharing
          setVendorModalOpen(false);

          // Log the final selections before processing
          console.log("Final selections before processing:", currentSelections);

          // Continue with the actual submission process
          const reportMetadata = await fetchReportMetadata(reportId);
          if (reportMetadata?.type === "invoice_report") {
            // Pass our local selections directly
            continueInvoiceSharingProcessWithSelections(
              currentSelections,
              currentPendingVendors
            );
          } else {
            continueSharingProcess();
          }
        }
      } else {
        console.log(`Vendor ${vendorName} not found, showing modal`);
        setVendorToCheck(vendorName);
        setVendorSuggestions(data.similarVendors || []);
        setVendorModalOpen(true);
      }
    } catch (error) {
      console.error("Vendor check failed:", error);
      toast.error("Failed to check vendor in QuickBooks");
    }
  };

  const continueInvoiceSharingProcessWithSelections = async (
    selections,
    vendorsArray = null
  ) => {
    console.log(
      "Starting invoice sharing process with selections:",
      selections
    );
    setIsSharing(true);
    try {
      // Use the utility function to ensure connection first
      await ensureConnected();

      // Use provided array or state
      const currentPendingVendors = vendorsArray || [...pendingVendors];

      // Check if we have all the vendor mappings needed
      const vendorMap = {};
      console.log("Building vendor map from:", {
        pendingVendors: currentPendingVendors,
        selections,
      });
      // Validate we have a selection for EVERY vendor before proceeding
      if (selections.length !== currentPendingVendors.length) {
        console.error("Vendor selection count mismatch", {
          selectionsCount: selections.length,
          vendorsCount: currentPendingVendors.length,
        });
        throw new Error(
          "Missing some vendor selections. Please complete all vendor selections."
        );
      }

      // Check for any null/undefined entries in selections array
      const missingSelections = [];
      for (let i = 0; i < selections.length; i++) {
        const selection = selections[i];
        if (!selection || !selection.id || !selection.name) {
          missingSelections.push(currentPendingVendors[i]);
        }
      }

      if (missingSelections.length > 0) {
        console.error(
          "Missing vendor selections for merchants:",
          missingSelections
        );
        throw new Error(
          `Missing vendor selection for: ${missingSelections.join(", ")}`
        );
      }

      // Use the current vendors array directly rather than state
      currentPendingVendors.forEach((merchantName, index) => {
        console.log(`Adding mapping for ${merchantName}:`, selections[index]);
        vendorMap[merchantName] = {
          id: selections[index].id,
          displayName: selections[index].name,
        };
      });

      console.log("Final vendor map:", vendorMap);

      if (Object.keys(vendorMap).length === 0) {
        throw new Error(
          "No vendor mappings available. Please select at least one vendor."
        );
      }

      // Send the invoice report to QuickBooks with vendor mappings
      const response = await fetch("/api/quickbooks/share-invoice-report", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          reportId,
          vendorMap,
          clientEmail: isClientOfAccountant ? clientEmail : undefined,
          useServiceAccount: !!reportMetadata?.isLocked,
        }),
      });

      // Add handling for the response data
      const data = await response.json();
      console.log("Invoice share response:", data);

      if (!response.ok) {
        // Handle errors similar to continueInvoiceSharingProcess
        if (data.requiresVendorSelection) {
          const vendorNameToUse = data.vendorName || "Unknown Vendor";
          console.log(
            "Vendor selection required for invoice:",
            vendorNameToUse
          );
          setVendorToCheck(vendorNameToUse);
          setVendorSuggestions(data.similarVendors || []);
          setVendorModalOpen(true);
          return;
        }

        if (response.status === 401 && data.reauthorizationUrl) {
          window.location.href = data.reauthorizationUrl;
          return;
        }

        throw new Error(
          data.error ||
            data.message ||
            "Failed to share invoice report to QuickBooks"
        );
      }

      // Handle success response
      const message = `Successfully processed ${data.results.length} vendors in QuickBooks`;
      console.log("Invoice share successful:", message);

      onStatusUpdate?.({
        status: "submitted",
        submittedAt: new Date().toISOString(),
        transactionIds: data.results
          .map((r) => r.transactionIds)
          .flat()
          .join(","),
        message: message,
      });

      toast.success(message, { autoClose: 3000 });
    } catch (error) {
      console.error("QuickBooks invoice sharing error:", error);
      setError(error.message);
      toast.error(error.message, { autoClose: 5000 });

      onStatusUpdate?.({
        status: "failed",
        error: error.message,
        submittedAt: new Date().toISOString(),
      });
    } finally {
      setIsSharing(false);
    }
  };

  const handleCreateVendor = async (vendorName) => {
    try {
      console.log(`Creating vendor: ${vendorName}`);
      const response = await fetch("/api/quickbooks/create-vendor", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ vendorName }),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || "Failed to create vendor");
      }

      // Only save to settings for regular expense reports, not invoice reports
      try {
        // First check if this is an invoice report
        const reportMetadata = await fetchReportMetadata(reportId);
        const isInvoiceReport = reportMetadata?.type === "invoice_report";

        // Only save the vendor to settings if this is NOT an invoice report
        if (!isInvoiceReport) {
          await fetch("/api/quickbooks/save-vendor-selection", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            body: JSON.stringify({
              vendorName: vendorName,
              vendorId: data.vendor.Id,
            }),
          });
          console.log("Vendor saved to settings (receipt flow)");
        } else {
          console.log("Invoice flow - not saving vendor to settings");
        }
      } catch (saveError) {
        console.error("Failed to save vendor selection:", saveError);
        // Continue anyway, not critical
      }

      toast.success(`${data.message || `Created vendor: ${vendorName}`}`);

      // Return vendor info so the modal can use it
      return {
        success: true,
        vendor: data.vendor,
      };
    } catch (error) {
      console.error("Vendor creation failed:", error);
      toast.error(`Failed to create vendor: ${error.message}`);
      throw error;
    }
  };

  // In ShareToQuickBooks.jsx - Replace the existing handleSelectVendor function
  const handleSelectVendor = async (vendor) => {
    if (!vendor) return;

    console.log(`handleSelectVendor called for: ${vendor?.name}`, {
      currentIndex: currentVendorIndex,
      totalVendors: pendingVendors.length,
      pendingVendors: pendingVendors,
    });

    // Get the current selections array
    const currentSelections = [...vendorSelections];

    // Store this selection
    currentSelections[currentVendorIndex] = {
      id: vendor.id,
      name: vendor.name,
    };

    // Update state for UI display
    setVendorSelections(currentSelections);

    toast.success(`Selected vendor: ${vendor.name}`);

    // Move to the next vendor
    const nextIndex = currentVendorIndex + 1;
    const currentPendingVendors = [...pendingVendors];

    console.log(`Advancing to next vendor index: ${nextIndex}`, {
      currentIndex: currentVendorIndex,
      nextIndex: nextIndex,
      pendingVendorsLength: currentPendingVendors.length,
      hasMoreVendors: nextIndex < currentPendingVendors.length,
      pendingVendors: currentPendingVendors,
      currentSelections: currentSelections,
    });

    if (nextIndex < currentPendingVendors.length) {
      // We have more vendors to process
      setCurrentVendorIndex(nextIndex);

      // Use the stored reference to pendingVendors and our current selections
      const nextVendor = currentPendingVendors[nextIndex];
      console.log(`Checking next vendor: ${nextVendor}`);
      checkVendor(
        nextVendor,
        currentPendingVendors,
        nextIndex,
        currentSelections
      );
    } else {
      // We've processed all vendors, close the modal
      console.log("All vendors processed, closing modal and continuing");
      setVendorModalOpen(false);

      // Continue with the actual submission process
      const reportMetadata = await fetchReportMetadata(reportId);
      console.log("Report metadata:", reportMetadata);
      if (reportMetadata?.type === "invoice_report") {
        console.log("Starting invoice sharing process");
        // Use the currentSelections directly
        continueInvoiceSharingProcessWithSelections(
          currentSelections,
          currentPendingVendors
        );
      } else {
        console.log("Starting regular sharing process");
        continueSharingProcess();
      }
    }
  };

  const continueSharingProcess = async () => {
    console.log("Continuing sharing process");
    setIsSharing(true);

    try {
      // Use the utility function
      await ensureConnected();

      // Rest of your sharing logic...

      // For receipt reports, check if we have vendor info from one of two sources:
      let vendorInfo;

      if (
        vendorSelections &&
        vendorSelections.length > 0 &&
        vendorSelections[0]
      ) {
        // Fix: Convert from {id, name} structure to {id, displayName} structure
        vendorInfo = {
          id: vendorSelections[0].id,
          displayName: vendorSelections[0].name,
        };
        console.log("Using vendor from selections:", vendorInfo);
      }
      // Option 2: From the vendorDisplayName (read from settings sheet)
      else if (vendorDisplayName) {
        // If vendor name exists in settings but not in selections, create a temporary vendor
        console.log(
          "Vendor found in settings, starting vendor selection flow:",
          vendorDisplayName
        );
        await handleVendorCreation([vendorDisplayName]);
        return; // Exit here - handleVendorCreation will call this function again after selection
      }

      console.log("Using vendor for report:", vendorInfo);

      // Proceed with share operation for receipt reports
      const shareResponse = await fetch("/api/quickbooks/share-report", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          reportId,
          vendorInfo,
          clientEmail: isClientOfAccountant ? clientEmail : undefined,
          useServiceAccount: !!reportMetadata?.isLocked,
        }),
      });
      const shareData = await shareResponse.json();
      console.log("Share report response:", shareData);

      if (!shareResponse.ok) {
        // Vendor selection required?
        if (shareData.requiresVendorSelection) {
          const vendorNameToUse = shareData.vendorName || "Unknown Vendor";
          console.log("Vendor selection required:", vendorNameToUse);
          setVendorToCheck(vendorNameToUse);
          setVendorSuggestions(shareData.similarVendors || []);
          setVendorModalOpen(true);
          return;
        }

        // Reauthorization required?
        if (shareResponse.status === 401 && shareData.reauthorizationUrl) {
          window.location.href = shareData.reauthorizationUrl;
          return;
        }

        // Invoice-specific validation errors
        if (shareData.errorCode === "INVALID_VENDORS") {
          setValidationErrors({
            invalidVendors: shareData.details.invalidVendors,
            sheetUrl: shareData.details.sheetUrl,
          });
          throw new Error(
            "Vendor validation failed. Please fix the errors and try again."
          );
        }

        // Any other error from the backend
        if (shareData.error) {
          throw new Error(shareData.error);
        }

        // Fallback generic error
        throw new Error("Sharing failed");
      }

      // If we reach here, the response is OK
      console.log("Share successful:", shareData);

      const isInvoiceReport = shareData.invoiceCount !== undefined;
      const message = isInvoiceReport
        ? `Successfully processed ${shareData.invoiceCount} invoices in QuickBooks`
        : "Successfully shared to QuickBooks";

      // On success, update status via callback
      onStatusUpdate?.({
        status: "submitted",
        submittedAt: new Date().toISOString(),
        billId: shareData.transactionId || shareData.billId,
        amount: shareData.amount,
        date: shareData.date,
        functionalTotal: shareData.functionalTotal,
        message: message,
      });

      toast.success(message, { autoClose: 3000 });
      return;
    } catch (error) {
      console.error("QuickBooks sharing error:", error);
      setError(error.message);

      // Check if the error comes directly from a fetch response
      if (
        error.message === "Expense validation failed" ||
        (error.message.includes("Found") &&
          error.message.includes("issues in expense data"))
      ) {
        // This is a functional total validation error - show the enhanced toast
        toast.error(
          <div>
            <strong>Missing Required Values</strong>
            <p>
              Some expenses are missing functional total values required for
              QuickBooks sync.
            </p>
            <p>Please open this report and add an FX Rate (See column V).</p>
            <a
              href={`https://docs.google.com/spreadsheets/d/${reportId}/edit`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "white",
                textDecoration: "underline",
                fontWeight: "bold",
                display: "block",
                marginTop: "8px",
              }}
            >
              Open Spreadsheet
            </a>
          </div>,
          { autoClose: 15000 } // Increased to 15 seconds for better visibility
        );
      } else {
        // Generic error toast for other errors
        toast.error(error.message || "An error occurred", { autoClose: 5000 });
      }

      // Also update status via callback
      onStatusUpdate?.({
        status: "failed",
        error: error.message || "An error occurred",
        submittedAt: new Date().toISOString(),
      });
    } finally {
      setIsSharing(false);
    }
  };

  const handleVendorResolved = async (vendorInfo) => {
    try {
      if (!vendorInfo || !vendorInfo.name) {
        throw new Error("Missing vendor information");
      }

      console.log("Vendor resolved:", vendorInfo);

      // Save the selected or created vendor to settings
      await fetch("/api/quickbooks/save-vendor-selection", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          vendorName: vendorInfo.name,
          vendorId: vendorInfo.id,
        }),
      });

      // Set the vendor name for next time
      setVendorDisplayName(vendorInfo.name);

      // Close modal first
      setVendorModalOpen(false);

      // Now update vendorSelections for continueSharingProcess to use
      const newSelections = [
        {
          id: vendorInfo.id,
          name: vendorInfo.name,
        },
      ];
      setVendorSelections(newSelections);

      // Wait a moment for state updates
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Retry the share operation
      await continueSharingProcess();
    } catch (error) {
      console.error("Error in handleVendorResolved:", error);
      setError(error.message);
      toast.error(error.message);
      setVendorModalOpen(false);
    }
  };

  const handleConnect = async () => {
    setIsConnecting(true);
    try {
      const currentPath = window.location.pathname + window.location.search;
      console.log("Initiating QuickBooks connection");

      // Add this line to set a flag
      localStorage.setItem("qb_connecting", "true");

      // Your existing code...
      if (isClientOfAccountant && clientEmail) {
        localStorage.setItem(
          "qbContext",
          JSON.stringify({
            isAccountantView: true,
            clientEmail: clientEmail,
            returnPath: currentPath,
          })
        );
      } else {
        localStorage.setItem(
          "qbContext",
          JSON.stringify({
            isAccountantView: false,
            returnPath: currentPath,
          })
        );
      }

      localStorage.setItem("selectedMenu", "expenseReports");

      const url = isClientOfAccountant
        ? `/api/quickbooks/connect?clientEmail=${encodeURIComponent(
            clientEmail
          )}&returnPath=${encodeURIComponent(currentPath)}`
        : `/api/quickbooks/connect?returnPath=${encodeURIComponent(
            currentPath
          )}`;

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!response.ok) {
        // Clear the flag if error
        localStorage.removeItem("qb_connecting");
        throw new Error("Failed to initiate connection");
      }

      const data = await response.json();
      if (data.authUri) {
        window.location.href = data.authUri;
      } else {
        // Clear the flag if error
        localStorage.removeItem("qb_connecting");
        throw new Error("No authorization URI received");
      }
    } catch (err) {
      console.error("Connection error:", err);
      // Clear the flag if error
      localStorage.removeItem("qb_connecting");
      setError("Failed to initiate QuickBooks connection");
      onConnectionStatusChange?.(false);
    } finally {
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (isDisconnecting) return;

    setIsDisconnecting(true);
    try {
      console.log("Disconnecting from QuickBooks");
      const response = await fetch("/api/quickbooks/disconnect", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to disconnect: ${response.status}`);
      }

      // Update both context and parent
      setIsConnected(false);
      onConnectionStatusChange?.(false);
    } catch (err) {
      console.error("Disconnect error:", err);
      setError("Failed to disconnect from QuickBooks");
      // Still update states on error
      setIsConnected(false);
      onConnectionStatusChange?.(false);
    } finally {
      setIsDisconnecting(false);
      setShowDisconnectConfirm(false);
    }
  };

  const ensureConnected = async () => {
    if (!isConnected) {
      throw new Error(
        "QuickBooks is not connected. Please connect to continue."
      );
    }

    const isStillConnected = await verifyConnection(true);
    if (!isStillConnected) {
      throw new Error(
        "QuickBooks connection lost. Please reconnect to continue."
      );
    }

    return true;
  };

  // New function to proceed with sharing after confirmation
  const confirmAndShare = async () => {
    setShowConfirmDialog(false);
    console.log("Share button clicked");
    setIsSharing(true);
    setMappingResults(null);
    setValidationErrors(null);
    setValidationIssues(null);

    try {
      // Use the utility function
      await ensureConnected();

      // For invoice reports, pre-check all vendors first
      const reportMetadata = await fetchReportMetadata(reportId);
      console.log("Report metadata:", reportMetadata);

      if (reportMetadata?.type === "invoice_report") {
        console.log("Processing invoice report, fetching merchants");
        // Fetch merchant names from the report
        const merchantsResponse = await fetch(
          isClientOfAccountant && clientEmail
            ? `/api/reports/${reportId}/merchants?clientEmail=${encodeURIComponent(
                clientEmail
              )}`
            : `/api/reports/${reportId}/merchants`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (!merchantsResponse.ok) {
          throw new Error("Failed to fetch report merchants");
        }

        const { merchants } = await merchantsResponse.json();
        console.log("Merchants found:", merchants);

        if (merchants && merchants.length > 0) {
          console.log(
            "Starting vendor creation workflow with merchants:",
            merchants
          );
          // Start vendor creation workflow
          await handleVendorCreation(merchants);
          return; // This will continue in continueSharingProcess after vendors are handled
        }
      } else {
        console.log("Processing regular expense report");
        // For expense reports, just check the report owner's name as vendor
        const vendorName = vendorDisplayName;
        if (vendorName) {
          console.log("Using report owner as vendor:", vendorName);
          await handleVendorCreation([vendorName]);
          return; // This will continue in continueSharingProcess after vendor is handled
        }
      }

      // If we're here, no vendors needed to be checked, proceed to continueSharingProcess
      // which will handle the case of needing to prompt for a vendor
      console.log("No vendors to check, proceeding directly");
      await continueSharingProcess();
    } catch (error) {
      // Error handling
      console.error("Error in handleShare:", error);
      setIsSharing(false);
      toast.error(error.message);
    }
  };
  // Inside your ShareToQuickBooks component
  // Inside your ShareToQuickBooks component
  const handleShare = async () => {
    // Show confirmation dialog first instead of starting the share process
    setShowConfirmDialog(true);
    // Reset any loading state that might be active
    setIsSharing(false);
  };

  // If already submitted, show submitted status
  if (quickbooks?.status === "submitted") {
    return (
      <div className="submitted-status flex items-center space-x-2">
        <i className="fas fa-check-circle text-green-500 text-xl"></i>
        <span className="text-green-500 font-medium">
          Submitted to QuickBooks
        </span>
      </div>
    );
  }

  // If not connected, show the official Connect to QuickBooks button
  if (!isConnected) {
    return (
      <div className="space-y-4">
        <button
          onClick={handleConnect}
          disabled={isConnecting}
          className="connect-to-quickbooks-button"
          style={{
            background: "none",
            border: "none",
            padding: 0,
            cursor: isConnecting ? "default" : "pointer",
            width: "208px", // Per QuickBooks guidelines - maintain 156x26 aspect ratio
            height: "35px",
            display: "inline-block",
          }}
        >
          {isConnecting ? (
            <div className="flex items-center justify-center">
              <TailSpin color="#4B5563" height={20} width={20} />
              <span className="ml-2">Connecting...</span>
            </div>
          ) : (
            <img
              src="/C2QB_green.png" // Update this path to where you store the SVG
              alt="Connect to QuickBooks"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
              }}
            />
          )}
        </button>
        {error && <p className="text-red-500">{error}</p>}
      </div>
    );
  }
  // Render confirmation dialog
  // Render confirmation dialog
  const renderConfirmDialog = () => {
    if (!showConfirmDialog) return null;

    // Check report type - use props.reportMetadata directly if available
    const isInvoiceReport =
      reportMetadata?.type === "invoice_report" ||
      reportMetadata?.reportType === "invoice_report";

    return createPortal(
      <div
        className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-center justify-center"
        onClick={() => setShowConfirmDialog(false)}
      >
        <div
          className="bg-white rounded-lg p-6 max-w-md w-full mx-4"
          onClick={(e) => e.stopPropagation()}
        >
          <h3 className="text-lg font-semibold mb-3">
            {isInvoiceReport
              ? "Share Invoices to QuickBooks"
              : "Share Report to QuickBooks"}
          </h3>

          <div className="mb-4 text-gray-700">
            {isInvoiceReport ? (
              <div>
                <p className="mb-2">
                  This will create a Bill in QuickBooks for{" "}
                  <strong>each merchant</strong> in the report.
                </p>
                <p className="mb-2">Each Bill will include:</p>
                <ul className="list-disc pl-5 mb-2">
                  <li>The original image of the invoice</li>
                  <li>All relevant details from the invoice</li>
                  <li>Additional details like PO, Invoice Number, Due Date</li>
                </ul>
                <p>
                  You'll have an opportunity to select or create vendors if they
                  don't already exist.
                </p>
              </div>
            ) : (
              <div>
                <p className="mb-2">
                  This will create a single Bill in QuickBooks for the{" "}
                  <strong>entire amount</strong> of this report.
                </p>
                <p className="mb-2">
                  The Bill will be payable to the report owner and will include:
                </p>
                <ul className="list-disc pl-5 mb-2">
                  <li>All expenses from this report</li>
                  <li>A link back to this report</li>
                </ul>
                <p>
                  You'll have an opportunity to select or create a vendor if it
                  doesn't already exist.
                </p>
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-3">
            <button
              className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              onClick={() => setShowConfirmDialog(false)}
            >
              Cancel
            </button>
            <button
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
              onClick={confirmAndShare}
            >
              Continue
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  };
  // Connected state - show share button and disconnect option
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <button
          onClick={handleShare}
          disabled={isSharing}
          className={`share-to-qb-btn share-state ${
            isSharing ? "disabled" : ""
          }`}
        >
          <div className="flex items-center justify-center space-x-2">
            {isSharing ? (
              <>
                <i className="fas fa-circle-notch fa-spin"></i>
                <span>Processing...</span>
              </>
            ) : (
              <>
                <i className="fab fa-quickbooks"></i>
                <span>Share to QuickBooks</span>
              </>
            )}
          </div>
        </button>
        <button
          onClick={() => setShowDisconnectConfirm(true)}
          className="disconnect-btn"
        >
          <i className="fas fa-power-off disconnect-icon"></i>
          <span className="disconnect-text">Disconnect</span>
        </button>
      </div>
      {/* Disconnect confirmation modal */}
      {showDisconnectConfirm &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999]"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }}
          >
            {/* Solid backdrop to prevent background color blending */}
            <div
              className="absolute inset-0 bg-[#00000080]" // Using hex with alpha instead of opacity
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
              }}
              onClick={() =>
                !isDisconnecting && setShowDisconnectConfirm(false)
              }
            />

            {/* Modal content with solid white background */}
            <div
              className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-white rounded-lg shadow-xl p-6"
              style={{
                position: "fixed",
                zIndex: 10000,
                backgroundColor: "#FFFFFF",
              }}
            >
              <h3 className="text-lg font-semibold text-gray-900">
                Confirm Disconnect
              </h3>
              <p className="mt-2 text-sm text-gray-600">
                Are you sure you want to disconnect from QuickBooks?
              </p>
              <div className="mt-4 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isDisconnecting) {
                      setShowDisconnectConfirm(false);
                    }
                  }}
                  disabled={isDisconnecting}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDisconnect();
                  }}
                  disabled={isDisconnecting}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md shadow-sm disabled:opacity-50"
                >
                  {isDisconnecting ? "Disconnecting..." : "Disconnect"}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
      {mappingResults && (
        <div className="mt-4">
          {/* ... rest of your mapping results code ... */}
        </div>
      )}
      {error && <p className="text-red-500">{error}</p>}
      {/* Add this before the vendor modal */}
      {validationErrors && (
        <div className="validation-errors mt-4 p-4 bg-red-100 border border-red-300 rounded-md">
          <h4 className="text-red-700 font-semibold mb-2">
            Vendor Validation Issues:
          </h4>
          <div className="error-list space-y-2">
            {validationErrors.invalidVendors?.map((error, idx) => (
              <div
                key={idx}
                className="error-item flex justify-between bg-white p-3 rounded border-l-4 border-red-500"
              >
                <div>
                  <span className="inline-block bg-gray-200 rounded px-2 py-1 text-xs mr-2">
                    Row {error.row}
                  </span>
                  <span className="font-medium">{error.value}</span>
                  <p className="text-gray-600 text-sm mt-1">{error.message}</p>
                  {error.suggestedVendors && (
                    <p className="text-blue-600 text-sm italic mt-1">
                      Suggestions: {error.suggestedVendors.join(", ")}
                    </p>
                  )}
                </div>
                <button
                  className="text-blue-500 hover:text-blue-700"
                  onClick={() =>
                    window.open(validationErrors.sheetUrl, "_blank")
                  }
                >
                  <i className="fas fa-external-link-alt"></i>
                </button>
              </div>
            ))}
          </div>
          <button
            className="mt-3 bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
            onClick={() => setValidationErrors(null)}
          >
            Re-validate After Fixes
          </button>
        </div>
      )}
      {/* VendorModal component */}

      <VendorModal
        isOpen={vendorModalOpen}
        onClose={(wasCanceled) => {
          console.log("Vendor modal closed, wasCanceled:", wasCanceled);
          setVendorModalOpen(false);
          if (wasCanceled) {
            // Reset loading state when user cancels
            setIsSharing(false);
            // Reset any partial vendor selections
            setPendingVendors([]);
            setVendorSelections([]);
            setCurrentVendorIndex(0);
          }
        }}
        onResolve={handleVendorResolved}
        onSelectVendor={handleSelectVendor}
        vendorName={vendorToCheck}
        similarVendors={vendorSuggestions}
        onCreateVendor={handleCreateVendor}
        pendingVendors={pendingVendors}
        currentVendorIndex={currentVendorIndex}
      />
      {renderConfirmDialog()}
    </div>
  );
};

export default ShareToQuickBooks;
